import { useCallback, useMemo, useState } from 'react';
import { differenceBy, map } from 'lodash';
import { FORM_MODES } from 'config/formModes';
import { MY_AUDIENCES_TYPES } from 'modules/campaigns/modules/common-campaign-form/config/MyAudiencesTypes';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { useRejectedAudiencesIndication } from '../../../../../../../audiences/hooks/useRejectedAudiencesIndication';
import TargetingTypes from '../../../../config/TargetingTypes';
import { audienceTypeToDefaultSimilarityLevel } from '../../utils/MyAudienceUtils';
import { useMyAudiencesApi } from './useMyAudiencesApi';
import { useMyAudiencesDefaultExcludeModal } from './useMyAudiencesDefaultExcludeModal';

export const useMyAudiences = accountId => {
    const { mode } = useFormDataContext();

    const { value: countryTargetingType, isDirty: countryTargetingTypeTouched } = useFormFieldValue({
        field: 'campaignTargeting.geoTargeting.countryTargeting.type',
    });
    const { value: countryTargetingValues, isDirty: countryTargetingValuesTouched } = useFormFieldValue({
        field: 'campaignTargeting.geoTargeting.countryTargeting.values',
    });

    const shouldInitExclude = useMemo(
        () => mode === FORM_MODES.CREATE && !countryTargetingTypeTouched && !countryTargetingValuesTouched,
        [countryTargetingTypeTouched, countryTargetingValuesTouched, mode]
    );

    const [targetingType, setTargetingType] = useState(TargetingTypes.INCLUDE);

    const {
        listOfMyAudiences,
        isSuccessMyAudiences,
        isSuccessAllMyAudiences,
        mapOfMyAudiences,
        defaultExcludedListOfMyAudience,
        isDisabled,
        isLoadingMyAudiences,
        mapOfAllMyAudiences,
    } = useMyAudiencesApi(accountId, countryTargetingType, countryTargetingValues, targetingType);

    const { openModal } = useMyAudiencesDefaultExcludeModal(mapOfAllMyAudiences);

    const { rejectedAudiences, isRejectedAudiencesSuccess } = useRejectedAudiencesIndication();

    const {
        value: includeList,
        onChange: setIncludeList,
        isDirty: myAudiencesTouched,
    } = useFormFieldValue({
        field: 'campaignTargeting.myAudienceTargeting.include',
    });
    const { value: excludeList, onChange: setExcludeList } = useFormFieldValue(
        {
            field: 'campaignTargeting.myAudienceTargeting.exclude',
            isPermitted: shouldInitExclude,
        },
        { defaultExcludedListOfMyAudience }
    );

    const includeAudiences = useMemo(
        () =>
            includeList.map(({ id }) => ({
                ...mapOfAllMyAudiences[id],
                targetingType: TargetingTypes.INCLUDE,
                audienceSize: mapOfMyAudiences[id]?.audienceSize || mapOfAllMyAudiences[id]?.audienceSize,
            })),
        [includeList, mapOfAllMyAudiences, mapOfMyAudiences]
    );

    const excludeAudiences = useMemo(() => {
        return map(excludeList, ({ id }) => ({ ...mapOfAllMyAudiences[id], targetingType: TargetingTypes.EXCLUDE }));
    }, [excludeList, mapOfAllMyAudiences]);

    const nonPbpAudiences = useMemo(() => {
        return listOfMyAudiences?.filter(audience => audience.type !== MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE);
    }, [listOfMyAudiences]);

    const pbpAudiences = useMemo(() => {
        return listOfMyAudiences?.filter(audience => audience.type === MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE);
    }, [listOfMyAudiences]);

    const hasIncludedNonPbpAudiences = useMemo(() => {
        return includeAudiences.some(audience => audience.type !== MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE);
    }, [includeAudiences]);

    const hasIncludedPbpAudiences = useMemo(() => {
        return includeAudiences.some(audience => audience.type === MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE);
    }, [includeAudiences]);

    const nonPBPIncludedItemsIds = useMemo(
        () =>
            new Set(
                listOfMyAudiences
                    ?.filter(audience => audience.type !== MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE)
                    .map(audience => audience.id)
            ),
        [listOfMyAudiences]
    );

    const setTargetingTypeValue = useCallback(({ value }) => {
        setTargetingType(value);
    }, []);

    const createNewAudience = useCallback(
        (audienceId, audience) => ({
            id: audienceId,
            similarityLevel: audienceTypeToDefaultSimilarityLevel[audience.type],
        }),
        []
    );

    const addItem = useCallback(
        ({ id: audienceId }) => {
            const selectedAudience = listOfMyAudiences.find(audience => audience.id === audienceId);
            const newAudience = createNewAudience(audienceId, selectedAudience);
            if (targetingType === TargetingTypes.INCLUDE) {
                setIncludeList(includeList => [...includeList, newAudience]);
            } else {
                setExcludeList(excludeList => [...(excludeList || []), newAudience]);
            }
        },
        [createNewAudience, listOfMyAudiences, setExcludeList, setIncludeList, targetingType]
    );

    const myAudiences = useMemo(() => {
        const selectedAudiences = [...includeAudiences, ...excludeAudiences];
        return differenceBy(listOfMyAudiences, selectedAudiences, 'id');
    }, [excludeAudiences, includeAudiences, listOfMyAudiences]);

    const removeIncludeAudience = useCallback(
        value => {
            setIncludeList(includeList.filter(audience => audience.id !== value));
        },
        [includeList, setIncludeList]
    );

    const [modalAlreadyOpened, setModalAlreadyOpened] = useState(false);
    const removeExcludeAudience = useCallback(
        async value => {
            const updatedExcludeList = await Promise.all(
                excludeList.map(async audience => {
                    const shouldKeep = audience.id !== value;
                    if (!shouldKeep) {
                        const shouldOpenModal = mapOfAllMyAudiences[audience.id]?.excludeFromCampaigns;
                        if (shouldOpenModal && !modalAlreadyOpened) {
                            const userIsSure = await openModal(audience);
                            if (userIsSure) {
                                setModalAlreadyOpened(userIsSure);
                                return false;
                            }
                            return true;
                        }
                    }
                    return shouldKeep;
                })
            );

            setExcludeList(excludeList.filter((_, index) => updatedExcludeList[index]));
        },
        [excludeList, mapOfAllMyAudiences, modalAlreadyOpened, openModal, setExcludeList]
    );

    const clearIncludedItems = useCallback(() => {
        setIncludeList([]);
    }, [setIncludeList]);

    const clearNonPBPIncludedItems = useCallback(() => {
        setIncludeList(includeList?.filter(audience => !nonPBPIncludedItemsIds.has(audience.id)));
    }, [includeList, nonPBPIncludedItemsIds, setIncludeList]);

    const clearExcludedItems = useCallback(async () => {
        const listContainsDefaultExclude = excludeList.some(({ id }) => mapOfAllMyAudiences[id]?.excludeFromCampaigns);
        let userIsSure = true;
        if (listContainsDefaultExclude) {
            userIsSure = await openModal();
        }
        if (userIsSure) {
            setExcludeList([]);
        }
    }, [excludeList, mapOfAllMyAudiences, openModal, setExcludeList]);

    return {
        includeAudiences,
        excludeAudiences,
        clearExcludedItems,
        clearIncludedItems,
        clearNonPBPIncludedItems,
        removeIncludeAudience,
        removeExcludeAudience,
        nonPbpAudiences,
        pbpAudiences,
        hasIncludedNonPbpAudiences,
        hasIncludedPbpAudiences,
        myAudiences,
        setTargetingTypeValue,
        addItem,
        isDisabled,
        isLoadingMyAudiences,
        targetingType,
        allSegmentsByIdMap: mapOfMyAudiences,
        isOptionsLoadedSuccessfully: !!(isSuccessMyAudiences && isSuccessAllMyAudiences),
        rejectedAudiencesList: rejectedAudiences,
        isRejectedAudiencesLoadedSuccessfully: isRejectedAudiencesSuccess,
        isDirty: myAudiencesTouched || countryTargetingValuesTouched,
        mode,
        createNewAudience,
    };
};
